/**
 * Calculates the radius in miles from bounds and center coordinates.
 * Uses the Haversine formula to find the maximum distance from center to any corner of the bounds.
 *
 * @param centerLat - Center latitude
 * @param centerLng - Center longitude
 * @param bounds - Bounding box with southwest and northeast corners
 * @returns Radius in miles
 */
export function getRadiusFromBounds(
  centerLat: number,
  centerLng: number,
  bounds: {
    southwest: { lat: number; lng: number };
    northeast: { lat: number; lng: number };
  },
): number {
  const EARTH_RADIUS_MILES = 3959; // Earth radius in miles

  // Calculate distances to all four corners of the bounds
  const corners = [
    { lat: bounds.southwest.lat, lng: bounds.southwest.lng }, // Southwest
    { lat: bounds.southwest.lat, lng: bounds.northeast.lng }, // Southeast
    { lat: bounds.northeast.lat, lng: bounds.southwest.lng }, // Northwest
    { lat: bounds.northeast.lat, lng: bounds.northeast.lng }, // Northeast
  ];

  // Find the maximum distance from center to any corner
  const distances = corners.map((corner) => {
    const dLat = ((corner.lat - centerLat) * Math.PI) / 180;
    const dLng = ((corner.lng - centerLng) * Math.PI) / 180;

    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos((centerLat * Math.PI) / 180) *
        Math.cos((corner.lat * Math.PI) / 180) *
        Math.sin(dLng / 2) *
        Math.sin(dLng / 2);

    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    return EARTH_RADIUS_MILES * c;
  });

  return Math.max(...distances);
}
